import { useId, useRef } from 'react';
import InVivoError from '@/model/InVivoError';
import { _isNil, _notNil } from '@/littledash';

export type UseCacheResponse<K, V> = {
  get: (key: K, force?: boolean) => Promise<V>;
  reset: () => void;
};
export type CacheLoadResponse<K, V> = {
  key: K;
  value: V;
  cache: boolean;
};
type LoadFunction<K, V> = (key: K) => Promise<CacheLoadResponse<K, V>>;

export const useCache = <K, V>(load: LoadFunction<K, V>): UseCacheResponse<K, V> => {
  const cache = useRef(new Map<K, V>());
  const loadRef = useRef<LoadFunction<K, V>>(load);
  const id = useId();
  loadRef.current = load;

  const _get = async (key: K, force = false) => {
    if (_isNil(key)) {
      return Promise.reject(new InVivoError('Invalid request: null key', { slug: 'use-cache' }));
    }
    const value = cache.current.get(key);
    if (!force && _notNil(value)) {
      return Promise.resolve(cache.current.get(key)!);
    }
    const currentLoad = loadRef.current;
    if (typeof currentLoad === 'function') {
      return currentLoad(key)
        .then((loadResponse) => {
          if (loadResponse.cache && _notNil(loadResponse.value)) {
            cache.current.set(loadResponse.key, loadResponse.value);
          }
          return loadResponse.value;
        })
        .catch((cause) => Promise.reject(new InVivoError('Load function failed', { cause, slug: 'use-cache' })));
    }
    return Promise.reject(new InVivoError('Invalid load or key function', { slug: 'use-cache' }));
  };
  const get = async (key: K, force = false) => {
    if (_notNil(navigator.locks)) {
      return await navigator.locks.request(`use-cache-${id}-${key}`, { mode: 'exclusive' }, async (lock) => {
        if (_notNil(lock)) {
          return await _get(key, force);
        }
        return Promise.reject(new InVivoError('Could not acquire lock', { slug: 'use-cache-lock-acquire' }));
      });
    } else {
      // Fallback for browsers that do not support the Locks API
      return await _get(key, force);
    }
  };

  return {
    get,
    reset: () => {
      cache.current.clear();
    },
  };
};
